import { CoachingSuggestion, RecommendationCard, ReportLink } from 'src/app/services/copilot/copilot.types';
import { PersonWeekEvidence } from './services/person-week-evidence';

export type Sensitivity = 'quiet' | 'balanced' | 'sensitive';

export type Severity = 'critical' | 'warning' | 'info' | 'ai';

export type AskAiScreen =
  'brief' | 'evidence' | 'personEvidence' | 'settings' | 'answer' | 'history' | 'watching' | 'agents' | 'handled' | 'views';

export type HistoryTab = 'active' | 'dismissed';

/**
 * One dismissal, recorded against the date-range + member selection it was made under: dismissing
 * only hides the signal while that same selection is picked, which is what the snackbar copy
 * promises. No reason is captured — dismissing hides signals, it doesn't teach the agent. Purely
 * local/instant-hide bookkeeping — History / Dismissed's actual record is server-side now (GET
 * activity/agent-gateway/signals?includeDismissed=true, see AskAiState.dismissedSignals).
 */
export interface SignalDismissal {
  signalId: string;
  filterKey: string;
}

// The mention composer's @-selection (MentionComposerSubmit.selection, parseMentionWire's
// AgentGatewayMentionSelection) — structurally identical, kept as Ask AI's own local type rather
// than importing the composer's/Copilot's so this file stays free of a cross-feature dependency.
// Threaded from SendQuestion through to the Agent Gateway request's `filters` (see
// AskAiState.sendQuestion/runLiveTurn) and stored on the turn so RetryConversationTurn can resend
// the same targeting.
// Structurally identical to the mention composer's @-selection, kept as Ask AI's own local type
// to avoid a cross-feature import. Threaded through to the Agent Gateway request's `filters` (see
// AskAiState.sendQuestion/runLiveTurn) and stored on the turn so retry can resend it.
export interface MentionSelection {
  selectedUsers?: Array<{ id: string, name?: string, timezone?: string }>;
  selectedGroups?: Array<{ id: string, name?: string }>;
}

export interface SignalTextPart {
  text?: string;
  link?: string;
  url?: string;
  // Paragraph break between runs — separates the finding from Recommendation/Next step lines.
  break?: boolean;
  // Renders this run bold — the "Recommendation:" and "Next step:" labels.
  bold?: boolean;
}

/** A Time Doctor Help Center article an answer points at. */
export interface DocLink {
  title: string;
  url: string;
}

/**
 * A "how do I…" answer: the user is asking how something works or how to handle it, not asking
 * anyone's data to be read. Matched on keywords, since these fixtures have no real retrieval.
 */
export interface GuidanceAnswer {
  keywords: string[];
  parts: SignalTextPart[];
  answer: string;
  docs: DocLink[];
}

export interface EvidenceRow {
  name: string;
  value: string;
  delta: string;
  detail: string;
  // Whether the viewer may see this row's user's screencasts — resolved once when the signal is
  // built (see buildGroupedSignal). Undefined on fixture rows, which fall back to the viewer's
  // own company-level setting.
  screencastsAllowed?: boolean;
}

/**
 * Threshold a Needs you card measures its people against — resolved once per signals load so
 * the card title, chart, and View details deltas all cite the same number Settings shows.
 */
export interface SignalThreshold {
  threshold: number;
  unit: string;
  direction: 'above' | 'below';
}

export interface SignalUserBar {
  id?: string;
  name: string;
  value: number;
  // Raw backend text for `value` (see AlertSeverityEntry.ratioDisplay) — preferred verbatim over
  // reformatting `value` wherever a display string is needed (row/body text, chart tooltip), so a
  // duration ratio shows exactly what the response sent ("8h 9m") rather than a reconstruction.
  display?: string;
}

/** One signal type's value on one day of a person card's chart (see Signal.personCardChart).
 * `isPeak` is this type's own peak day, not necessarily the card's overall worst day. */
export interface PersonCardDailyBar {
  label: string; // short per-type label for the tooltip, e.g. 'Idle', 'Heavy'
  breaching: boolean; // severity !== 'ok' that day, real (see UserAlertEntry.severity)
  isPeak: boolean;
  displayValue: string; // native-unit value, formatted, for the tooltip
  // False when this type has data for other days but not this one (the live response is sparse).
  // Rendered as a distinct "not evaluated" slot rather than guessed as breaching or not.
  hasData: boolean;
}

/** One weekday column of a person card's chart — the five company-chart weekdays, same as the
 * precomputed cards (see buildPersonCardDailyChart). Has a bar per type with any real per-day data on the
 * card; a type built entirely from the range-aggregate fallback gets no bar at all. */
export interface PersonCardDailyColumn {
  day: string; // short weekday, 'Mon'..'Sun'
  bars: PersonCardDailyBar[];
}

export interface Signal {
  // Synthetic — `${alertId}-${severity}` (see buildGroupedSignal), not a real backend id. The
  // backend's POST activity/agent-gateway/signals/status has never heard of it; dismiss/undo send
  // `signalIds` below instead.
  id: string;
  key: string;
  type: string;
  // Individual alert-type labels behind `type` — set only on person-card signals, whose `type` is
  // several labels joined with "and" for display. Code that dedupes/counts types across cards (see
  // urgentSignalTypes/signalSummary) must use this, not `type`, or shared types get double-counted.
  types?: string[];
  sev: Severity;
  // The real per-person signalIds (AlertSeverityEntry.signalId) behind this grouped card — a
  // group can represent several people, so dismissing/undoing the whole card marks every one of
  // them, not just this card's own synthetic `id`. See AskAiState.dismissSignal/undoDismissSignal.
  signalIds: string[];
  // Person-week cards only: the saved week this card belongs to, so History / Dismissed can label a
  // row with its own period rather than whatever range Needs you has selected.
  weekStart?: string;
  weekEnd?: string;
  // Set only on a pinned-from-Ask-AI card, whose title is the user's own question rather than a
  // templated one — elsewhere it's computed by signalTitle() (see ask-ai-signal-title.ts).
  title?: string;
  body: string;
  // idle_time/heavy_hours ignore this in favor of a computed body (see `teamLabel` etc. below);
  // kept as a plain-text fallback since every other alert type still renders it as authored.
  parts: SignalTextPart[];
  answerParts: SignalTextPart[];
  answer: string;
  trend: boolean;
  barThreshold?: number;
  chartCaption: string;
  bars: number[];
  // Needs you's threshold mini-chart. Kept separate from `bars`/`barThreshold` above, which still
  // back the Analyze conversation's own day-series chart.
  userCount: number;
  userName?: string;
  userBars: SignalUserBar[];
  threshold: number;
  unit: string;
  // unusual_activity/manual_time/mobile_time only: their `ratio` is really an hours+minutes
  // duration (see AlertSeverityEntry.ratio), so rows/body text/the threshold chart render it as
  // "8h 09m" (formatDurationAmount) instead of unit-based formatThresholdAmount's rounded "8.2h".
  durationRatio?: boolean;
  direction: 'above' | 'below';
  // Feed the computed Needs you bodies for the templated alert types (see *SignalParts in
  // ask-ai-signal-body.ts), rather than hand-typed `parts` below. No backend source yet — fixtures
  // set them directly; a real response should populate them once it carries team name/size/streak.
  teamLabel?: string; // e.g. 'Sales team' — shown instead of "{n} users" for a team-scope card
  teamSize?: number; // total members in scope, for the "N of M members" line; defaults to userCount when unset
  // Consecutive weeks over the line — the streak sentence for a person or team body.
  streakWeeks?: number;
  // unusual_activity only: what the flagged members' activity has in common, for the team body's
  // "same {sharedPatternLabel}" clause. Unset omits the clause rather than guessing.
  sharedPatternLabel?: string;
  // unusual_activity only: 'week' (default) reads a weekly streak; 'day' compares today against
  // the rest of the week, since a single-day spike has no meaningful week-over-week streak.
  evaluationPeriod?: 'day' | 'week';
  // Person-card only — per-day breakdown behind the card's badge. Gates the brief card's day
  // chart, reusing showTrend/toggleTrend's existing open-state.
  personCardChart?: PersonCardDailyColumn[];
  // Person-card only — sentences kept separate rather than joined into `parts` via `{break: true}`,
  // since that gap is styled for finding/recommendation pairs, not parallel sentences. `parts`/
  // `answerParts` still carry the flattened version for consumers needing one flat array.
  personCardBodyLines?: SignalTextPart[][];
  // Person-card only — the week matrix View details renders for this user (see person-week-evidence.ts).
  personWeekEvidence?: PersonWeekEvidence;
  askPrompt: string;
  // Backs "Draft a message": the reason it's worth sending, the editable body, and follow-ups.
  recommendation: string;
  draftMessage: string;
  followUps: string[];
  reportName: string;
  reportUrl: string;
  chipContext: string;
  evidenceCount: number;
  period: string;
  metricLabel: string;
  evBody: string;
  rows: EvidenceRow[];
  rowLabel?: string;
  single?: boolean;
  pinned?: boolean;
  // Set only via PinAnswerToBrief — lets dismiss/undo-dismiss find the source turn to keep its
  // briefId (its Add/See in brief icon) in sync with whether the card is still in the brief.
  pinnedConversationId?: string;
  pinnedTurnId?: string;
}

export interface AlertTypeRule {
  sensitive: string;
  balanced: string;
  quiet: string;
}

export interface ThresholdSetting {
  condition: 'above' | 'below';
  value: string;
}

/**
 * Per-alert-type config the severity + sensitivity components render — mirrors the backend's
 * severity engine shape (T/unit/direction, plus x/y offsets reserved for a future severity-band
 * feature). `previews` can be generated from this via `buildPreviews`; `rules` stays hand-authored.
 */
export interface SeveritySensitivityConfig {
  threshold: number; // T — user-configurable
  unit: string; // '%', 'h', 'events', 'entries', 'days'...
  direction: 'above' | 'below';
  x: number; // reserved for a future severity-band computation — unused by rendering today
  y: number; // reserved for a future severity-band computation — unused by rendering today
}

export interface SensitivityNotchLabels {
  sensitive: string;
  balanced: string;
  quiet: string;
}

/**
 * Config for deriving an alert type's preview sentences from `severity` at render time (see
 * `buildPreviews`), instead of hand-typing all three notches. Only fits the templated "over an
 * amount" shape — one that doesn't (e.g. attendance) authors an `AlertTypeRule` directly instead.
 */
export interface PreviewOptions {
  label: string; // how the alert type reads in a sentence, e.g. 'idle time', 'heavy hours'
  frame?: 'state' | 'week'; // "whenever it is over" vs "whenever a week goes over" — default 'week'
  // Word shown next to the x/y delta, e.g. 'points' for idle. Omit for the default: '%'/single-
  // letter units attach to the number ("4h+"), word units stay bare ("4+ over").
  deltaWord?: string;
  deltaHasOver?: boolean; // whether the delta parenthetical ends in "over" — default true
}

export interface AlertType {
  key: string;
  name: string;
  // Subject + verb for condition copy, e.g. "Weekly hours are above" — kept separate from `name`
  // since they don't always match ("Heavy hours" the title vs "Weekly hours" the subject).
  subject: string;
  subjectVerb: 'is' | 'are';
  severity: SeveritySensitivityConfig;
  // Condition directions the threshold dropdown offers — explicit per type so a future one can
  // be restricted to above-only/below-only.
  supportedDirections: ('above' | 'below')[];
  notchLabels?: SensitivityNotchLabels; // defaults to Sensitive/Balanced/Quiet when omitted
  rules: AlertTypeRule; // short per-notch copy, e.g. "About 10+ points over" — authored per alert type
  // Full preview sentence per notch — either the literal copy, or PreviewOptions for the
  // component to build from `severity` at render time via buildPreviews.
  previews: AlertTypeRule | PreviewOptions;
  counts: AlertTypeRule; // live per-notch match counts — data, not config
  mode: Sensitivity; // notch currently applied server-side — seeds sensitivityByAlertType
  isEnabled: boolean; // passed through from the backend's AlertTypeResponse.isEnabled
  // From AlertTypeResponse.defaultSensitivity — kept alongside `severity.x`/`.y` so a sensitivity
  // PATCH can send the real {name, value} pair, 'sensitive' included (x/y only cover two notches).
  defaultSensitivity: SensitivityThreshold[];
}

export interface SeverityBandLimit {
  min: number;
  max: number | null;
}

/** One entry per user the threshold applies to; `userId: null` is the company-wide default used
 * when no per-user override exists for the current user. */
export interface SeverityConfigResponse {
  userId: string | null;
  threshold: number;
  operator: 'above' | 'below';
  // Severity-band cutoffs (info/warning/critical) — reserved for the same future severity-band
  // feature as `SeveritySensitivityConfig.x`/`y`, not consumed yet.
  exceedLimits: {
    info: SeverityBandLimit;
    warning: SeverityBandLimit;
    critical: SeverityBandLimit;
  };
}

/** The threshold one sensitivity notch would apply. Used both in
 * `AlertTypeResponse.defaultSensitivity` and as `AlertTypeSeverityConfig.sensitivity`. */
export interface SensitivityThreshold {
  name: Sensitivity;
  value: number;
}

/**
 * One severityConfigs entry — SeverityConfigResponse plus the notch (`sensitivity`) currently
 * applied. Used by AlertTypeResponse only; SignalsResponse carries no severityConfigs.
 */
export interface AlertTypeSeverityConfig extends SeverityConfigResponse {
  sensitivity: SensitivityThreshold;
}

/**
 * GET activity/agent-gateway/alerts' real response shape for one alert type. `id` is what this
 * app calls `key` elsewhere. `isCreated` is false for a never-configured alert type —
 * `severityConfigs` is empty until then, and `default*` is what a create call seeds it with (see
 * AskAiService.provisionUncreatedAlerts).
 */
export interface AlertTypeResponse {
  id: string;
  title: string;
  suggestedCopilotPrompt: string;
  defaultOperator: 'above' | 'below';
  defaultThreshold: number;
  defaultMetric: string;
  defaultExceedLimits: {
    info: SeverityBandLimit;
    warning: SeverityBandLimit;
    critical: SeverityBandLimit;
  };
  defaultSensitivity: SensitivityThreshold[];
  isCreated: boolean;
  isEnabled: boolean;
  severityConfigs: AlertTypeSeverityConfig[];
  createdAt: string;
  updatedAt: string;
}

/** One flagged user for an alert type/date range — `id` is a userId, resolved to a display name
 * by AskAiService.getSignals via ResolveService. `signalId`/`analyzedCount` aren't consumed yet;
 * reserved for a future drill-into-the-signal link. */
export interface AlertSeverityEntry {
  id: string;
  severity: Severity;
  ratio: number;
  // The raw `ratio` text as the response sent it, kept only when it arrived as a duration string
  // ("8h 9m", "0m", "<1m" — unusual_activity/manual_time/mobile_time) rather than a plain number.
  // `ratio` above still parses that same string into decimal hours for math (sorting, threshold
  // comparison, chart scaling) — this is what display renders instead, verbatim, so a value never
  // gets reformatted away from what the backend actually sent.
  ratioDisplay?: string;
  signalId: string;
  analyzedCount: number;
  // Only present once a caller has acted on this signal (dismissed it, or undone that) — an
  // entry that's never been touched carries no `status` at all. Omitted from a plain GET (no
  // includeDismissed), since that call excludes dismissed entries entirely; with
  // includeDismissed=true the response mixes active and dismissed together, distinguished by this.
  status?: AlertSeverityStatus;
}

// `updatedBy`/`updatedAt` record who last called POST activity/agent-gateway/signals/status for
// this signal and when (a dismiss or an undo) — not consumed yet (see AlertSignalGroup and
// AskAiService.getDismissedSignals, which only reads `status`), reserved for a future "dismissed
// by X on Y" line.
export interface AlertSeverityStatus {
  status: 'active' | 'dismissed';
  updatedBy: string;
  updatedAt: string;
}

/**
 * GET activity/agent-gateway/signals' response shape. No threshold/sensitivity config travels
 * with it — that comes from activity/agent-gateway/alerts instead. `signals` has one entry per
 * alert type with results, keyed by that type's id, bucketing entries by severity (including
 * 'ok', which parseSignalsResponse drops before it becomes a Signal card).
 */
export interface SignalsBySeverity {
  ok?: AlertSeverityEntry[];
  info?: AlertSeverityEntry[];
  warning?: AlertSeverityEntry[];
  critical?: AlertSeverityEntry[];
}

export interface SignalsResponse {
  signals: Record<string, SignalsBySeverity>[];
  // AA-247 — precomputed Needs-you person-week cards, present when the company has a saved week
  // (see backend personWeeksToSignalsResponse). Absent on a plain live response.
  personWeeks?: unknown[];
  source?: string;
  weekStart?: string;
  // Only on the no-range "latest signals" list (AA-327): cards matching the caller across all pages.
  total?: number;
}

// ---- AA-247 saved person-week cards ---------------------------------------------------------
// Parsed shape of one card in SignalsResponse.personWeeks. Mirrors the backend PersonWeekCard so
// the Needs you brief can render Iulian's card design from real data instead of fixtures.

export const STORED_REVIEW_PAGES = [
  'timeline',
  'activity-summary',
  'hours-tracked',
  'unusual-activity',
  'edit-time',
] as const;

export type StoredReviewPage = typeof STORED_REVIEW_PAGES[number];

export interface StoredPersonWeekCta {
  key: string;
  label: string;
  href: string;
  /** Review evidence destination. Absent on cards written before this field. */
  page?: StoredReviewPage;
}

export interface StoredPersonWeekMetricValue {
  value: number;
  unit: string;
  display?: string;
}

export interface StoredPersonWeekLimit {
  value: number;
  unit: string;
}

export interface StoredPersonWeekSignal {
  type: string;
  severity: Severity;
  daysOverLimit: number;
  limit: StoredPersonWeekLimit;
  peakDay: string; // ISO yyyy-mm-dd of the peak day
  peakValue: StoredPersonWeekMetricValue;
  deepLink?: string;
  firedDates: string[]; // ISO yyyy-mm-dd for every day this signal fired
  /** Backend raw ids for POST activity/agent-gateway/signals/status when dismissing this type. */
  signalIds?: string[];
}

export interface StoredPersonWeekDayIssue {
  type: string;
  severity: Severity;
  value: StoredPersonWeekMetricValue;
  limit: StoredPersonWeekLimit;
  deepLink?: string;
}

/** One daily snapshot. `metrics` keeps the raw upstream fields; the adapter derives native-unit
 * per-signal values from them for the View details week matrix. */
export interface StoredPersonWeekDay {
  date: string; // ISO yyyy-mm-dd
  metrics: Record<string, number>;
  issues: StoredPersonWeekDayIssue[];
}

/**
 * One precomputed write-up under `analysisByThreshold`. Keys are threshold fingerprints
 * (`sha256:…`) or threshold-owner user ids — the viewer picks the matching entry (see
 * pickPersonWeekAnalysis).
 */
export interface StoredPersonWeekAnalysis {
  status: 'none' | 'ready' | 'failed';
  analyzedAt?: string;
  model?: string;
  title: string;
  body: string;
  longTitle?: string;
  description?: string;
  signalsNamed: string[];
  contextChecked: string[];
  /** Opening user bubble when Analyze / follow-up opens chat. */
  followUpQuestion?: string;
  /** Opening assistant answer for that followUpQuestion (precomputed; not the card body). */
  followUpAnswer?: string;
  dataCaveat?: string | null;
  draftMessage?: string;
  primaryCta?: StoredPersonWeekCta;
  secondaryCta?: StoredPersonWeekCta | null;
}

export interface StoredPersonWeek {
  userId: string;
  userName: string;
  weekStart: string;
  weekEnd: string;
  timezone?: string;
  severity?: Severity;
  rank?: number | null;
  title: string;
  /** Card prose on Needs you (not the chat opening answer). */
  body: string;
  analysisStatus: 'none' | 'ready' | 'failed';
  /** Suggested note saved with the card. Empty on weeks written before this field. */
  draftMessage?: string;
  /** Week-level chat opening answer when present (threshold map may override). */
  followUpAnswer?: string;
  primaryCta?: StoredPersonWeekCta;
  secondaryCta?: StoredPersonWeekCta | null;
  signals: StoredPersonWeekSignal[];
  days: StoredPersonWeekDay[];
  /** Union of every signal's firedDates — drives the card mini-chart's marked days. */
  firedDates: string[];
  /** Raw ids for POST activity/agent-gateway/signals/status: the week id (`…_person_week`) and,
   * once the feed is merged, any type id that didn't match a signal on this card. */
  signalIds?: string[];
  /**
   * True when this card is hidden for the caller (status dismissed). Present on
   * GET …/signals?includeDismissed=true; omitted from the default (active) Needs you list.
   */
  dismissed?: boolean;
  /**
   * Pre-analyzed write-ups keyed by threshold config (fingerprint or owner user id). Card still
   * uses week.title/body; chat seeds followUpQuestion + followUpAnswer from the matching entry.
   */
  analysisByThreshold?: Record<string, StoredPersonWeekAnalysis>;
}

/** One alert type's flagged results, re-bucketed by severity by parseSignalsResponse ('ok'
 * excluded). buildSignalsFromSignalsResponse builds one grouped Signal card per (alertId,
 * severity) bucket, rather than one Signal per person. */
export interface AlertSignalGroup {
  alertId: string;
  bySeverity: Partial<Record<Severity, AlertSeverityEntry[]>>;
}

/** Same entry GET activity/agent-gateway/signals returns, minus `id` (groupBy=user keys by
 * userId instead) plus `alertId` (which the by-severity envelope keyed by instead). No per-day
 * breakdown travels with it — see ask-ai-person-signals.util.ts for what person cards do with it. */
export interface UserAlertEntry {
  alertId: string;
  // 'ok' is kept here (unlike AlertSeverityEntry.severity, which drops it) — per-day aggregation
  // needs every day, not just fired ones, to compute days-evaluated and the daily chart.
  severity: Severity | 'ok';
  ratio: number;
  ratioDisplay?: string; // see AlertSeverityEntry.ratioDisplay
  signalId: string;
  analyzedCount: number;
  // Lowercase full weekday name as the backend sends it (e.g. "tuesday"). Optional — the backend
  // hasn't finished backfilling it onto every entry yet. An entry with no/invalid `day` still
  // parses (just can't contribute to per-day stats) rather than being dropped, so a partially
  // tagged response doesn't silently empty the whole brief.
  day?: string;
  status?: AlertSeverityStatus; // see AlertSeverityEntry.status; omitted unless includeDismissed=true
}

/** One user's fired alerts for the range — GET activity/agent-gateway/signals with groupBy=user
 * re-bucketed by parseUserGroupedSignalsResponse ('ok' excluded, same as parseSignalsResponse). */
export interface UserSignalGroup {
  userId: string;
  entries: UserAlertEntry[];
}

/** GET activity/agent-gateway/signals?groupBy=user's response shape — one entry per user, each
 * holding a flat list of fired/unfired alerts (see UserAlertEntry), unlike SignalsResponse's
 * per-alert-type severity buckets. */
export interface UserGroupedSignalsResponse {
  signals: Record<string, unknown>[];
}

/**
 * Frontend-authored copy for one alert type — whatever AlertTypeResponse has no source for (name/
 * grammar, notch copy that doesn't fit buildPreviews). Keyed in ALERT_TYPE_COPY; mapAlertTypeResponses
 * merges it in per live response, falling back to a generic derived version (buildRules,
 * resolvePreviews) for any id with no entry — hence `rules`/`previews`/`counts` are all optional.
 */
export interface AlertTypeCopy {
  name: string;
  subject: string;
  subjectVerb: 'is' | 'are';
  supportedDirections: ('above' | 'below')[];
  notchLabels?: SensitivityNotchLabels;
  rules?: AlertTypeRule;
  previews?: AlertTypeRule | PreviewOptions;
  counts?: AlertTypeRule;
}

export interface Period {
  key: string;
  label: string;
}

export interface ConversationTurnStep {
  text: string;
  tool: string;
  // Set on a step reported by a live call's tool_start/tool_end events. Undefined for
  // fixture-built steps, which fall back to the position-based `turn.step` counter instead (see
  // AnalyzeConversationComponent.stepMark).
  done?: boolean;
}

// Single-select, all-optional reason chips shown after a not_helpful rating. Keyed values are
// stored/tracked; labels are translated separately (askAi.feedback.reasons.*).
export type AskAiFeedbackReason = 'numbers_wrong' | 'not_what_i_asked' | 'missing_context' | 'too_long' | 'disagree';

export const ASK_AI_FEEDBACK_REASONS: readonly { key: AskAiFeedbackReason, labelKey: string }[] = [
  { key: 'numbers_wrong', labelKey: 'askAi.feedback.reasons.numbersWrong' },
  { key: 'not_what_i_asked', labelKey: 'askAi.feedback.reasons.notWhatIAsked' },
  { key: 'missing_context', labelKey: 'askAi.feedback.reasons.missingContext' },
  { key: 'too_long', labelKey: 'askAi.feedback.reasons.tooLong' },
  { key: 'disagree', labelKey: 'askAi.feedback.reasons.disagree' },
];
export interface ConversationTurn {
  id: string;
  q: string;
  step: number;
  done: boolean;
  steps: ConversationTurnStep[];
  answer: string;
  answerParts: SignalTextPart[];
  reportName: string;
  reportUrl: string;
  chipContext: string;
  sigId: string;
  evidenceOpen: 'none' | 'trend' | 'people' | 'draft';
  // Set on guidance turns instead of chart/breakdown/draft actions: no one's data is behind the
  // answer, so the actions link out to the articles it drew on.
  docs?: DocLink[];
  briefId?: string;
  // The manager's edits to the suggested message. Unset until they type — the textarea falls
  // back to the signal's own draftMessage, so "revert" is just clearing this field.
  draft?: string;
  // Shown locally right away; a live turn's feedback is also submitted to the Agent Gateway (see
  // AskAiService.submitTurnFeedback, keyed off requestId) — fixture turns stay local-only.
  feedbackRating?: 'helpful' | 'not_helpful';
  // Optional reason chip chosen after a not_helpful rating. Held locally alongside feedbackRating.
  feedbackReason?: AskAiFeedbackReason;
  // Real token usage for a live call (see AskAiState.runLiveTurn); otherwise estimated from the
  // question/answer length for fixture turns — see AskAiService.estimateUsage.
  usage?: TokenUsage;
  // One-line preview of the answer streamed so far, shown inside the trace once the answer is
  // slow to land. The card is withheld for the whole reasoning phase, so this is the only glimpse
  // of the answer while the user waits. Cleared on the final patch — see AskAiState.runLiveTurn.
  answerPreview?: string;
  // True while a live call is still streaming this turn's answer, so the card can reveal
  // progressively. Fixture turns never set this; they stay gated on `done` only.
  pending?: boolean;
  // Set when a live call fails outright. `answer`/`answerParts` still carry the user-facing
  // error message so the existing card just renders it.
  error?: boolean;
  // False means don't offer a retry affordance (permanent failure, e.g. not configured/quota).
  // Undefined for fixture turns, which never error.
  retryable?: boolean;
  // Agent Gateway's correlation id, needed to submit thumbs up/down feedback (see
  // AskAiService.submitTurnFeedback). Undefined for fixture turns.
  requestId?: string;
  // The @-mention this turn's question was sent with, persisted so RetryConversationTurn can
  // resend the exact same targeting rather than just the signal-derived filters.
  mentionSelection?: MentionSelection;
  // True for a turn *attempted* as a real Agent Gateway call, even if it failed and fell back to
  // guidanceFallback — so the conversation keeps trying live on its next question instead of
  // getting stuck on the fixture path (see AskAiState.sendQuestion's conversationIsLive). `answer`
  // is raw markdown when live actually answered; `answerParts` otherwise (fixture or fallback).
  live?: boolean;
  // Set alongside `live: true` when a live call failed and local Help Center guidance was
  // substituted (see guidanceFor). Purely a rendering flag: routes this turn through
  // `answerParts` since guidance content isn't markdown. Never true together with `error`.
  guidanceFallback?: boolean;
  reportLinks?: ReportLink[];
  filters?: Record<string, unknown>;
  recommendationCard?: RecommendationCard;
  coachingSuggestions?: CoachingSuggestion[];
}

export interface TokenUsage {
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
}

export interface Conversation {
  id: string;
  sigId?: string;
  title: string;
  started: string;
  updated: string;
  turns: ConversationTurn[];
  // The Agent Gateway's own conversation id (distinct from `id`, purely local) — echoed back by
  // the first live turn and resent on later turns so the gateway keeps multi-turn memory.
  // Undefined until a first live turn succeeds; never set for a fixture-only conversation.
  gatewayConversationId?: string;
  // The card a thread loaded from the server belongs to. Such a thread carries no sigId (that would
  // change scope locking, pinning and dismissal for it) and no turn filters, so the person-week card
  // matches it through this instead. Only set when the thread was found by signalId + weekStart.
  personWeek?: { userId: string, weekStart: string };
  // The user/group scope (see userScopeKey in store/state.ts) active when this conversation was
  // started. A signal keeps the same id across a scope change (see buildSignalsFromSignalsResponse),
  // but its content — team-level vs a single user — doesn't, so a conversation from a stale scope
  // must not be resumed as if it still answered for the card's current content.
  scopeKey?: string;
}

/**
 * One row of GET activity/agent-gateway/agent/chat/conversations — the signed-in caller's chat
 * history, distinct from the local-only {@link Conversation}. See AskAiService.listConversations.
 */
export interface AgentChatConversationSummary {
  conversationId: string;
  header: string;
  messageCount: number;
  updatedAt: string;
  // The signal (a person-week card's userId) and Monday the thread was started for; absent on
  // threads not tied to a card.
  signalId?: string;
  weekStart?: string;
}

/** Envelope GET activity/agent-gateway/agent/chat/conversations responds with. */
export interface AgentChatConversationsResponse {
  data: AgentChatConversationSummary[];
  total: number;
}

/**
 * A turn's stored feedback, returned per assistant message by the gateway and matched to the
 * message via `meta.requestId`. Lets a reopened conversation show the vote the user already
 * gave, instead of a blank thumb — see buildConversationFromHistoryDetail.
 */
export interface AgentChatMessageFeedback {
  rating: 'helpful' | 'not_helpful';
  reason?: AskAiFeedbackReason;
  requestId: string;
  messageId: string;
  updatedAt: string;
}

/** One entry of GET activity/agent-gateway/agent/chat/conversations/{conversationId}'s `messages`. */
export interface AgentChatConversationMessage {
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
  // Only seen on `user` messages, identical to `content` there — unused (title comes from the
  // list row's own `header`, or the detail response's own `header` below as a fallback).
  header?: string;
  // `requestId` is the gateway's own id for the turn that produced this message — the only turn
  // identity that survives a reload (a local `turn.id` is rebuilt from array position), so it is
  // what feedback is stored against.
  meta?: { generatedBy?: string, tool?: string, requestId?: string, [key: string]: unknown };
  feedback?: AgentChatMessageFeedback;
}

/**
 * GET activity/agent-gateway/agent/chat/conversations/{conversationId} — one conversation's full
 * history. Confirmed against a live call  — see AskAiService.getConversation.
 * `page`/`limit`/`total` paginate `messages` itself (unused today — nothing here requests a
 * non-default page, and no conversation has exceeded one page in practice yet).
 */
export interface AgentChatConversationDetail {
  conversationId: string;
  header: string;
  messages: AgentChatConversationMessage[];
  page: number;
  limit: number;
  total: number;
  updatedAt: string;
}

/**
 * Fixed id/title for the one conversation started from the "What the AI Agent can do" guide
 * dialog's example chips. Never forked and its title isn't user-editable — see
 * AnalyzeConversationComponent's isGuideConversation checks.
 */
export const ASK_AI_GUIDE_CONVERSATION_ID = 'ai-agent-guide';
export const ASK_AI_GUIDE_CONVERSATION_TITLE = 'What the AI Agent can do';

export interface Toast {
  msg: string;
  undo?: () => void;
}
